import React from 'react';
import { GraphConfigProps, BarChartConfig } from '../../../../types/graph-builder.types';
import { createColumnId, ColumnId } from '../../../../types';
import { getCategoricalColumns, getIntervalRatioColumns } from '../../../utils/graph-builder-utils';
import * as styles from '../../../styles/components/graph-builder.css.ts';

/**
 * BarChartの設定UIコンポーネント
 */
export const BarChartConfigComponent: React.FC<GraphConfigProps<BarChartConfig>> = ({ 
  config, 
  dataset, 
  onChange 
}) => {
  // カテゴリカラムの選択肢（nominal/ordinal尺度）
  const categoryColumns = getCategoricalColumns(dataset);
  
  // 数値カラム（interval/ratio尺度のみ）
  const numericColumns = getIntervalRatioColumns(dataset);
  
  // 選択された値列のセット（重複選択を防ぐため）
  const selectedValues = new Set(config.valueColumns || []);
  
  const handleCategoryColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      categoryColumn: createColumnId(e.target.value)
    });
  };
  
  const handleAddValueColumn = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) return;

    const newValue = value === '$count' ? value : createColumnId(value) as ColumnId | '$count';
    const currentValues = config.valueColumns || [];

    // 重複チェック
    if (currentValues.includes(newValue)) {
      e.target.value = ''; // セレクトをリセット
      return;
    }

    const newValues = [...currentValues, newValue];
    const newAggregations = { ...(config.aggregations || {}) };

    // デフォルトの集計方法を設定
    if (value === '$count') {
      newAggregations[value] = 'count';
    } else {
      newAggregations[value] = 'average';
    }

    onChange({
      ...config,
      valueColumns: newValues,
      aggregations: newAggregations
    });

    e.target.value = ''; // セレクトをリセット
  };
  
  const handleRemoveValueColumn = (valueToRemove: ColumnId | '$count') => {
    const newValues = (config.valueColumns || []).filter(v => v !== valueToRemove);
    const newAggregations = { ...(config.aggregations || {}) };

    const key = valueToRemove === '$count' ? valueToRemove : String(valueToRemove);

    delete newAggregations[key];

    onChange({
      ...config,
      valueColumns: newValues,
      aggregations: newAggregations
    });
  };
  
  const handleAggregationChange = (valueColumn: string, aggregation: string) => {
    onChange({
      ...config,
      aggregations: {
        ...(config.aggregations || {}),
        [valueColumn]: aggregation as 'sum' | 'average' | 'min' | 'max' | 'count'
      }
    });
  };
  
  const handleOrientationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      orientation: e.target.value as 'vertical' | 'horizontal'
    });
  };
  
  const handleStackModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      stackMode: e.target.value as 'grouped' | 'stacked'
    });
  };
  
  const handleShowValuesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      showValues: e.target.checked
    });
  };

  const handleSortOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      sortOrder: e.target.value as 'alphabetical' | 'frequency-desc' | 'frequency-asc'
    });
  };

  const handleTopNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = value === '' ? undefined : parseInt(value, 10);
    onChange({
      ...config,
      topN: numValue
    });
  };

  return (
    <>
      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Category Column</label>
        <select
          value={config.categoryColumn || ''}
          onChange={handleCategoryColumnChange}
          className={styles.settingSelect}
          data-testid="barchart-category-select"
        >
          <option value="">Select a column</option>
          {categoryColumns.length === 0 ? (
            <option value="" disabled>No categorical columns (nominal/ordinal)</option>
          ) : (
            categoryColumns.map(col => (
              <option key={col.id} value={col.id}>
                {col.name} ({col.type}{col.scale?.scale ? `, ${col.scale.scale}` : ''})
              </option>
            ))
          )}
        </select>
      </div>
      
      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Values</label>
        
        {/* 選択された値列のリスト */}
        {config.valueColumns && config.valueColumns.length > 0 && (
          <div className={styles.selectedValuesList}>
            {config.valueColumns.map((valueCol, index) => {
              const valueKey = valueCol === '$count' ? valueCol : String(valueCol);
              const columnInfo = valueCol === '$count' ? 'Count (Frequency)' :
                               numericColumns.find(c => c.id === valueCol)?.name || valueCol;

              return (
                <div key={`${valueKey}-${index}`} className={styles.selectedValueItem}>
                  <div className={styles.valueHeader}>
                    <span className={styles.valueName}>{columnInfo}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveValueColumn(valueCol)}
                      className={styles.removeButton}
                      aria-label="Remove value"
                    >
                      ×
                    </button>
                  </div>

                  {/* 集計方法の選択（$count以外） */}
                  {valueCol !== '$count' && (
                    <select
                      value={config.aggregations?.[valueKey] || 'average'}
                      onChange={(e) => handleAggregationChange(valueKey, e.target.value)}
                      className={styles.settingSelectSmall}
                    >
                      <option value="sum">Sum</option>
                      <option value="average">Average</option>
                      <option value="min">Min</option>
                      <option value="max">Max</option>
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* 新しい値列を追加するセレクト */}
        <select
          onChange={handleAddValueColumn}
          className={styles.settingSelect}
          value=""
          data-testid="barchart-value-select"
        >
          <option value="">Add a value...</option>
          <option value="$count" disabled={selectedValues.has('$count')}>
            Count (Frequency)
          </option>
          <optgroup label="Numeric Columns">
            {numericColumns.length === 0 ? (
              <option value="" disabled>No numeric columns with interval/ratio scale</option>
            ) : (
              numericColumns.map(col => (
                <option
                  key={col.id}
                  value={col.id}
                  disabled={selectedValues.has(createColumnId(col.id))}
                >
                  {col.name} {col.scale?.scale ? `(${col.scale.scale})` : ''}
                </option>
              ))
            )}
          </optgroup>
        </select>
      </div>
      
      {/* 複数値列が選択されている場合のみ表示 */}
      {config.valueColumns && config.valueColumns.length > 1 && (
        <div className={styles.settingItem}>
          <label className={styles.settingLabel}>Display Mode</label>
          <select
            value={config.stackMode || 'grouped'}
            onChange={handleStackModeChange}
            className={styles.settingSelect}
          >
            <option value="grouped">Grouped</option>
            <option value="stacked">Stacked</option>
          </select>
        </div>
      )}
      
      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Orientation</label>
        <select
          value={config.orientation || 'vertical'}
          onChange={handleOrientationChange}
          className={styles.settingSelect}
        >
          <option value="vertical">Vertical</option>
          <option value="horizontal">Horizontal</option>
        </select>
      </div>

      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Sort Order</label>
        <select
          value={config.sortOrder || 'alphabetical'}
          onChange={handleSortOrderChange}
          className={styles.settingSelect}
        >
          <option value="alphabetical">Alphabetical</option>
          <option value="frequency-desc">Value (High to Low)</option>
          <option value="frequency-asc">Value (Low to High)</option>
        </select>
      </div>

      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Top N Categories</label>
        <input
          type="number"
          value={config.topN || ''}
          onChange={handleTopNChange}
          placeholder="Show all (leave empty)"
          min="1"
          className={styles.settingInput}
        />
      </div>

      <div className={styles.settingItem}>
        <label className={styles.settingCheckbox}>
          <input
            type="checkbox"
            checked={config.showValues || false}
            onChange={handleShowValuesChange}
          />
          <span>Show value labels</span>
        </label>
      </div>
    </>
  );
};